'use client';

import React, { RefObject, useRef } from 'react';

import './styles/@theme';
import { classes } from '@/utils/classes/classes';
import { useAccountUm } from '@/hooks/use-account-um';
import { useFavoriteGames } from '@/hooks/use-favorite-games';
import { useAsyncLock } from '@/hooks/use-async-lock';
import { POST_LOGIN_ACTION_TYPE, setPostLoginAction } from '@/utils/post-login-action/post-login-action';
import { useStore } from '@/store/store';
import { SvgLoader } from '@/app/elements/svg/loader';
import { FAVORITES_VARIANT } from '@/app/elements/favorites-button/favorites-button.constants';
import { getFavoriteGameProps, getTrackingData } from '@/tracking/amplitude/events/general/favorite-game';
import { useZoneLayout } from '@/app/components/zones/contexts/zone-layout-context';
import { PRISMIC_ZONE_LAYOUT } from '@/app/constants/zone';
import { useGlobalContext } from '@/store/contexts/global-context';
import { PRODUCT_THROTTLES } from '@/utils/feature-flags/constants';
import { usePathname } from 'next/navigation';
import { useCategoryTracking } from '@/tracking/snowplow/contexts/category-tracking-context';
import { EVENT_TYPE } from '@/tracking/snowplow/utils';
import { sendGameElementSnowplowEvent } from '@/tracking/snowplow/send-game-element-snowplow-event';

export type FavoritesButtonProps = {
    uid: string;
    variant?: FAVORITES_VARIANT.NORMAL | FAVORITES_VARIANT.OUTLINED;
    isRenderRequired?: boolean; // true for game spotlight zone where we always want if the feature flag permits
    trackingModule?: string;
};

export const FavoritesButton = ({
    uid,
    variant = FAVORITES_VARIANT.NORMAL,
    isRenderRequired,
    trackingModule
}: FavoritesButtonProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const { triggerLogin } = useAccountUm();
    const context = useZoneLayout();
    const { featureFlags, isLoggedIn } = useGlobalContext();
    const FAVORITES_SIZE = 24;
    const pathname = usePathname();
    const { favoriteGames, addFavoriteGame, removeFavoriteGame } = useFavoriteGames();
    const { runExclusive } = useAsyncLock();
    const { sendEvent } = useStore((state) => state.tracking);
    const activePopup = useStore((state) => state.popup?.activePopup);
    const categoryTracking = useCategoryTracking();

    const isFavorite = Array.isArray(favoriteGames) && favoriteGames.includes(uid);
    const layout = context?.layout ?? PRISMIC_ZONE_LAYOUT.DEFAULT;

    if (!featureFlags[PRODUCT_THROTTLES.FAVOURITE_GAMES]) {
        return;
    }

    const shouldShowFavoritesButton =
        isRenderRequired || // show when especially required to (e.g. animated trailer)
        (layout === PRISMIC_ZONE_LAYOUT.DEFAULT && // or if it has layout default and
            (pathname !== '/' || // show on all routes except homepage
                (pathname === '/' && !isLoggedIn))); // show on homepage only when logged out

    if (!shouldShowFavoritesButton) {
        return;
    }

    const toggleFavorite = runExclusive(async () => {
        if (isFavorite) {
            await removeFavoriteGame(uid);
        } else {
            await addFavoriteGame(uid);
        }
    });

    const sendTrackingEvents = () => {
        const trackingData = getTrackingData(ref as RefObject<HTMLElement>);
        const modalOriginModule =
            trackingData?.module === 'game_info_modal'
                ? (activePopup?.params?.originModule as string | undefined)
                : undefined;
        const amplitudeEvent = getFavoriteGameProps({
            action: isFavorite ? 'remove' : 'add',
            module: trackingModule ?? modalOriginModule ?? trackingData?.module ?? undefined,
            zoneName: trackingData?.zoneName || '',
            gameName: trackingData?.gameName || '',
            position: trackingData?.gamePosition ? parseInt(trackingData?.gamePosition) : undefined,
            zonePosition: trackingData?.zonePosition ? parseInt(trackingData?.zonePosition) : undefined,
            isSwjGame: trackingData?.isSwjGame,
            gameId: uid
        });

        sendEvent([{ destination: 'amplitude', eventProps: amplitudeEvent }]);

        const snowplowEventType = isFavorite ? EVENT_TYPE.GAME_FAVORITE_REMOVED : EVENT_TYPE.GAME_FAVORITED;
        const zoneLabel = categoryTracking.categoryName || trackingData?.zoneName || trackingData?.module || '';

        const hasCategoryContext = Boolean(categoryTracking.categoryName);

        void sendGameElementSnowplowEvent({
            sendEvent,
            eventType: snowplowEventType,
            eventProps: {},
            trackingData: trackingData
                ? {
                      gamePosition: trackingData.gamePosition ?? null,
                      zonePosition: trackingData.zonePosition ?? null,
                      zoneType: hasCategoryContext ? 'category_grid' : null,
                      zoneLayout: null,
                      gameName: trackingData.gameName ?? null,
                      zoneName: trackingData.zoneName ?? null,
                      module: trackingData.module ?? null
                  }
                : null,
            gameUid: uid,
            gameName: trackingData?.gameName ?? null,
            zoneName: zoneLabel,
            zoneInteractionContext: hasCategoryContext
                ? {
                      action: null,
                      zoneType: 'category_grid',
                      zoneName: categoryTracking.categoryName,
                      pageName: categoryTracking.categoryName,
                      zoneId: categoryTracking.zoneUid || null,
                      zonePosition: null,
                      zoneLayout: null,
                      ctaLabel: null
                  }
                : undefined
        });
    };

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        sendTrackingEvents();

        if (isLoggedIn) {
            toggleFavorite();
        } else {
            setPostLoginAction({ type: POST_LOGIN_ACTION_TYPE.ADD_FAVORITE, uid });
            triggerLogin();
        }
    };

    return (
        <div
            ref={ref}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            aria-pressed={isFavorite}
            className={classes(
                'favorites-button',
                variant === FAVORITES_VARIANT.OUTLINED && FAVORITES_VARIANT.OUTLINED,
                isFavorite && 'active'
            )}>
            {variant === FAVORITES_VARIANT.OUTLINED ? (
                <SvgLoader iconName="favorites" />
            ) : (
                <SvgLoader iconName="favorite-game" width={FAVORITES_SIZE} height={FAVORITES_SIZE} />
            )}
        </div>
    );
};
